import { Alert, Container, Stack, Title } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { ReactElement } from 'react';
import { HubShell } from '@/components/Hub/HubShell';
import { TabOption } from '@/components/Timeline/TimelineTabs';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { PayablesTab } from './payables/PayablesTab';
import { ReceivablesTab } from './receivables/ReceivablesTab';
import { useReceivables } from './receivables/useReceivables';
import { RevenueTab } from './RevenueTab';
import { WORK_COUNT_STYLE } from './workCount';

export type FinanceOpsTab = 'revenue' | 'receivables' | 'payables';

export function FinanceOpsPage(): ReactElement {
  const {
    currentUserPermissions: { canViewFinanceOps },
  } = useCurrentUser();

  if (!canViewFinanceOps) {
    return (
      <Container size="xl" py="md">
        <Alert icon={<IconAlertCircle size={16} />} title="Access Denied" color="red">
          This page is only accessible to Finance and administrators.
        </Alert>
      </Container>
    );
  }

  return <FinanceOpsHub />;
}

function FinanceOpsHub(): ReactElement {
  // The Receivables count is work, so it shows from any tab (BAR-3038).
  const receivables = useReceivables();
  const tabs: TabOption<FinanceOpsTab>[] = [
    { label: 'Revenue', icon: null, value: 'revenue' },
    {
      label: 'Receivables',
      icon: null,
      value: 'receivables',
      // A failed read is not "nothing to invoice" (BAR-3125).
      indicator: receivables.error ? (
        <span style={WORK_COUNT_STYLE} title="Couldn't load receivables">
          !
        </span>
      ) : (
        receivables.toInvoiceCount > 0 && <span style={WORK_COUNT_STYLE}>{receivables.toInvoiceCount}</span>
      ),
    },
    { label: 'Payables', icon: null, value: 'payables' },
  ];

  return (
    <Container size="xl" py="md" style={{ height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column' }}>
      <Stack gap="lg" style={{ flex: 1, overflow: 'hidden' }}>
        <Title order={1}>Finance ops</Title>
        <HubShell tabs={tabs} defaultTab="revenue">
          {(activeTab) =>
            activeTab === 'receivables' ? (
              <ReceivablesTab receivables={receivables} />
            ) : activeTab === 'payables' ? (
              <PayablesTab />
            ) : (
              <RevenueTab />
            )
          }
        </HubShell>
      </Stack>
    </Container>
  );
}
